import { MAX_GPX_BYTES, getSpeedColor, haversine, percentile, readFileAsText } from "./utils.js";

const TRACK_WEIGHT = 4;

export async function loadGpxFromFile(file) {
  const gpxText = await readFileAsText(file);
  return loadGpxFromText(gpxText);
}

export async function loadGpxFromUrl(urlValue) {
  const url = normalizeGpxUrl(urlValue);
  const response = await fetch(url.href, { credentials: "same-origin" });

  if (!response.ok) {
    throw new Error(`Unable to fetch GPX file (${response.status}).`);
  }

  const contentLength = response.headers.get("Content-Length");
  if (contentLength && Number(contentLength) > MAX_GPX_BYTES) {
    throw new Error("GPX file is too large.");
  }

  const gpxText = await response.text();
  if (gpxText.length > MAX_GPX_BYTES) {
    throw new Error("GPX file is too large.");
  }

  return loadGpxFromText(gpxText);
}

function loadGpxFromText(gpxText) {
  const xml = new DOMParser().parseFromString(gpxText, "application/xml");
  const parserError = xml.querySelector("parsererror");
  if (parserError) {
    throw new Error("Invalid GPX XML.");
  }

  const speedSegments = buildSpeedSegments(xml);
  if (!speedSegments.length) {
    throw new Error("The GPX file needs timestamped track points to calculate speed.");
  }

  const speeds = speedSegments.map((segment) => segment.speed);
  const minSpeed = percentile(speeds, 0.05);
  const maxSpeed = percentile(speeds, 0.95);
  const layer = L.featureGroup();
  const profile = buildSpeedProfile(speedSegments);

  speedSegments.forEach((segment) => {
    L.polyline(
      [
        [segment.from.latitude, segment.from.longitude],
        [segment.to.latitude, segment.to.longitude],
      ],
      {
        color: getSpeedColor(segment.speed, minSpeed, maxSpeed),
        lineCap: "round",
        lineJoin: "round",
        opacity: 0.95,
        smoothFactor: 1,
        weight: TRACK_WEIGHT,
      }
    ).addTo(layer);
  });

  parsePointNodes(xml, "wpt").forEach((point) => {
    L.circleMarker([point.latitude, point.longitude], {
      radius: 4,
      weight: 2,
      opacity: 1,
      fillOpacity: 0.85,
    }).addTo(layer);
  });

  return {
    layer,
    bounds: layer.getBounds(),
    profile,
  };
}

function buildSpeedSegments(xml) {
  const trackSegments = getNodes(xml, "trkseg");
  const pointGroups = trackSegments.length
    ? trackSegments.map((segmentNode) => parseTrackPoints(segmentNode))
    : [parseTrackPoints(xml)];
  const speedSegments = [];

  pointGroups.forEach((points) => {
    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const current = points[index];
      const seconds = (current.time.getTime() - previous.time.getTime()) / 1000;

      if (seconds <= 0) {
        continue;
      }

      const distance = haversine(previous, current);
      const speed = distance / (seconds / 3600);

      if (!Number.isFinite(speed)) {
        continue;
      }

      speedSegments.push({
        from: previous,
        to: current,
        distance,
        seconds,
        speed,
      });
    }
  });

  return speedSegments;
}

function buildSpeedProfile(speedSegments) {
  const totalDistance = speedSegments.reduce((total, segment) => total + segment.distance, 0);
  const totalSeconds = speedSegments.reduce((total, segment) => total + segment.seconds, 0);
  const averageSpeed = totalSeconds > 0 ? totalDistance / (totalSeconds / 3600) : 0;

  return {
    totalDistance,
    totalSeconds,
    averageSpeed,
    kmSplits: buildKmSplits(speedSegments, totalDistance),
  };
}

function buildKmSplits(speedSegments, totalDistance) {
  const splitCount = Math.ceil(totalDistance);
  if (!splitCount) {
    return [];
  }

  const splits = Array.from({ length: splitCount }, (_, index) => ({
    index: index + 1,
    startDistance: index,
    endDistance: Math.min(index + 1, totalDistance),
    distance: 0,
    seconds: 0,
    averageSpeed: 0,
    pathSegments: [],
  }));

  let cumulativeDistance = 0;
  speedSegments.forEach((segment) => {
    if (segment.distance <= 0) {
      splits[getSplitIndex(cumulativeDistance, splits.length)].seconds += segment.seconds;
      return;
    }

    const segmentStart = cumulativeDistance;
    const segmentEnd = cumulativeDistance + segment.distance;
    let currentDistance = segmentStart;

    while (currentDistance < segmentEnd) {
      const nextKm = Math.floor(currentDistance) + 1;
      const chunkEnd = Math.min(nextKm, segmentEnd);
      const chunkDistance = chunkEnd - currentDistance;
      const split = splits[getSplitIndex(currentDistance, splits.length)];
      const chunkStartRatio = (currentDistance - segmentStart) / segment.distance;
      const chunkEndRatio = (chunkEnd - segmentStart) / segment.distance;

      split.distance += chunkDistance;
      split.seconds += segment.seconds * (chunkDistance / segment.distance);
      split.pathSegments.push([
        pointToLatLng(interpolateSegmentPoint(segment, chunkStartRatio)),
        pointToLatLng(interpolateSegmentPoint(segment, chunkEndRatio)),
      ]);
      currentDistance = chunkEnd;
    }

    cumulativeDistance = segmentEnd;
  });

  splits.forEach((split) => {
    split.averageSpeed = split.seconds > 0 ? split.distance / (split.seconds / 3600) : 0;
  });

  return splits.filter((split) => split.distance > 0 || split.seconds > 0);
}

function interpolateSegmentPoint(segment, ratio) {
  return {
    latitude: segment.from.latitude + (segment.to.latitude - segment.from.latitude) * ratio,
    longitude: segment.from.longitude + (segment.to.longitude - segment.from.longitude) * ratio,
  };
}

function pointToLatLng(point) {
  return [point.latitude, point.longitude];
}

function getSplitIndex(distance, splitCount) {
  if (distance >= splitCount) {
    return splitCount - 1;
  }

  return Math.max(0, Math.floor(distance));
}

function parseTrackPoints(root) {
  return getNodes(root, "trkpt")
    .map((node) => {
      const timeNode = getNodes(node, "time")[0];
      const latitude = Number(node.getAttribute("lat"));
      const longitude = Number(node.getAttribute("lon"));
      const time = timeNode ? new Date(timeNode.textContent) : null;

      return { latitude, longitude, time };
    })
    .filter(
      (point) =>
        Number.isFinite(point.latitude) &&
        Number.isFinite(point.longitude) &&
        point.time instanceof Date &&
        !Number.isNaN(point.time.getTime())
    );
}

function parsePointNodes(xml, tagName) {
  return getNodes(xml, tagName)
    .map((node) => ({
      latitude: Number(node.getAttribute("lat")),
      longitude: Number(node.getAttribute("lon")),
    }))
    .filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
}

function getNodes(root, tagName) {
  const namespaced = root.getElementsByTagNameNS
    ? Array.from(root.getElementsByTagNameNS("*", tagName))
    : [];

  return namespaced.length ? namespaced : Array.from(root.getElementsByTagName(tagName));
}

function normalizeGpxUrl(urlValue) {
  if (!urlValue || urlValue.length > 2048) {
    throw new Error("Invalid GPX URL.");
  }

  let url;
  try {
    url = new URL(urlValue, window.location.origin);
  } catch (error) {
    throw new Error("Invalid GPX URL.");
  }

  if (url.origin !== window.location.origin) {
    throw new Error("Only GPX files from this site can be loaded.");
  }

  if (!["http:", "https:"].includes(url.protocol)) {
    throw new Error("Invalid GPX URL protocol.");
  }

  if (!url.pathname.toLowerCase().endsWith(".gpx")) {
    throw new Error("The URL must point to a .gpx file.");
  }

  return url;
}
